// Merging replicated documents into the live Zustand store.
//
// Replication drops incoming docs into the local PouchDB; a changes feed then
// re-reads them and hands the result here. The store cannot simply be replaced
// with that snapshot: the FE edits customers/visits/inspections optimistically
// and shadow-writes to PouchDB asynchronously, so a snapshot taken mid-write is
// older than what the user is looking at, and overwriting it would revert their
// edit (and unmount the page they are on).
//
// The rule is therefore per-entity, not per-slice: take an incoming entity only
// when it is genuinely newer than the store's copy (`updatedAt`, stamped by the
// store-adapter on every write) and no local write for it is still in flight.
// That lets an edit made on another device appear without a page reload —
// previously only entities the store had never seen were merged, so updates
// stayed invisible until the next reload (client #68).
//
// Deletions are not applied here: an entity that vanished from the snapshot is
// indistinguishable from one whose creation has not been written yet. Removing
// a whole customer/visit still needs a reload on the other device.

import type { InitPartial } from './init-store';

export interface Entity {
  id: string;
  updatedAt?: number;
}

/** True when a local write for this entity id has not settled yet. */
export type IsBusy = (id: string) => boolean;

/**
 * Merge `incoming` into `cur` by id. Returns the new array, or null when
 * nothing changed (so callers can skip a pointless store write + re-render).
 */
export function mergeById<T extends Entity>(
  cur: T[],
  incoming: T[],
  isBusy: IsBusy
): T[] | null {
  const byId = new Map(cur.map(e => [e.id, e]));
  const added: T[] = [];
  let changed = false;

  for (const next of incoming) {
    const prev = byId.get(next.id);
    if (!prev) {
      added.push(next);
      changed = true;
      continue;
    }
    // A write of our own is still settling — the snapshot predates it.
    if (isBusy(next.id)) continue;
    if ((next.updatedAt ?? 0) > (prev.updatedAt ?? 0)) {
      byId.set(next.id, next);
      changed = true;
    }
  }
  if (!changed) return null;
  // Keep the store's existing order; appended entities go last.
  return [...cur.map(e => byId.get(e.id) ?? e), ...added];
}

interface MergeableState {
  customers: Entity[];
  visits: Entity[];
  inspections: Entity[];
}

/**
 * Turn a freshly-read PouchDB snapshot into the slice patch that is safe to
 * push into the store. Read-mostly catalog slices pass through untouched; the
 * edit-authoritative slices go through mergeById.
 */
export function buildSafePartial(
  current: MergeableState,
  incoming: InitPartial,
  isBusy: IsBusy
): InitPartial {
  const safe: InitPartial = {};

  // Catalogs are only written from admin screens, never mid-render, so the
  // replicated snapshot is always the better copy.
  if (incoming.manufacturers) safe.manufacturers = incoming.manufacturers;
  if (incoming.deviceTypes) safe.deviceTypes = incoming.deviceTypes;
  if (incoming.spareParts) safe.spareParts = incoming.spareParts;
  if (incoming.servicePrices) safe.servicePrices = incoming.servicePrices;
  if (incoming.workers) safe.workers = incoming.workers;
  if (incoming.settings) safe.settings = incoming.settings;
  if (incoming.hoseConfig) safe.hoseConfig = incoming.hoseConfig;

  if (incoming.customers?.length) {
    const merged = mergeById(
      current.customers,
      incoming.customers as unknown as Entity[],
      isBusy
    );
    if (merged) safe.customers = merged as InitPartial['customers'];
  }
  if (incoming.visits?.length) {
    const merged = mergeById(
      current.visits,
      incoming.visits as unknown as Entity[],
      isBusy
    );
    if (merged) safe.visits = merged as InitPartial['visits'];
  }
  if (incoming.inspections?.length) {
    const merged = mergeById(
      current.inspections,
      incoming.inspections as unknown as Entity[],
      isBusy
    );
    if (merged) safe.inspections = merged as InitPartial['inspections'];
  }
  return safe;
}
